import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import MapArea from '../Components/MapArea'; 
import './add_patient.css'; 
import { AddPatientMobileStep2 } from "./add_patient_mobile";
import { API_BASE_URL } from '../config.js';

export const SYRIAN_GOVERNORATES = [
  "ريف دمشق", "دمشق", "حلب", "حمص", "حماة", "اللاذقية", 
  "طرطوس", "إدلب", "الرقة", "دير الزور", "الحسكة", "درعا", "السويداء", "القنيطرة"
];

export default function AddPatientStep2() {
  const navigate = useNavigate();
  const location = useLocation();
  const step1Data = location.state || {};
  
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  const [formData, setFormData] = useState({ 
    city: "", 
    address: "", 
    blood_group: "",
    residence_lat: null,
    residence_long: null
  });

  const [showMapModal, setShowMapModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); 
  const [statusMessage, setStatusMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    if (!step1Data.name) navigate("/add-patient"); 
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [step1Data, navigate]);

  const handleChange = (e) => {
    if (isSubmitting) return; 
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value.replace(/[<>"']/g, "") }));
  };

  const openMapForAddress = () => {
    setShowMapModal(true);
  };

 const handleMapConfirm = async (lat, lng) => {
    setShowMapModal(false);
    setStatusMessage({ type: "loading", text: "جاري تحليل الموقع ومطابقته مع المحافظة..." });

    try {
      // أضفنا addressdetails=1 لجلب تفاصيل الموقع بدقة
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=ar&addressdetails=1`);
      if (res.ok) {
        const data = await res.json();
        
        // استخراج اسم المحافظة من الخريطة
        const addressObj = data.address || {};
        const fullAddressStr = Object.values(addressObj).join(" "); // دمج تفاصيل العنوان كنص واحد
        
        // البحث عن المحافظة السورية المطابقة في النص
        const detectedCity = SYRIAN_GOVERNORATES.find(gov => fullAddressStr.includes(gov));

        // 1. التحقق الذكي: إذا اختار محافظة لا تتطابق مع الخريطة!
        if (formData.city && detectedCity && formData.city !== detectedCity) {
          setStatusMessage({ type: "error", text: `تنبيه: الموقع المحدد (${detectedCity}) لا يتطابق مع المحافظة التي اخترتها (${formData.city}). يرجى التصحيح.` });
          return; // نرفض الموقع ولا نحفظه
        }

        // 2. تجربة مستخدم سحرية: إذا لم يقم باختيار المحافظة، نختارها له تلقائياً!
        let updatedCity = formData.city;
        if (!formData.city && detectedCity) {
          updatedCity = detectedCity;
        }

        const shortAddress = data.display_name.split(',').slice(0, 3).join('، ');
        
        // تحديث البيانات إذا اجتازت التحقق
        setFormData(prev => ({ 
          ...prev, 
          city: updatedCity, 
          address: shortAddress, 
          residence_lat: lat, 
          residence_long: lng 
        }));
        
        setStatusMessage({ type: "success", text: "تم تحديد العنوان ومطابقته بنجاح." });
        setTimeout(() => setStatusMessage({ type: "", text: "" }), 2500); 
      } else {
        throw new Error("Geocoding Failed");
      }
    } catch (err) {
      // في حال فشل الخادم أو الإنترنت، نقبل الموقع كإحداثيات مبدئية
      setFormData(prev => ({ ...prev, residence_lat: lat, residence_long: lng, address: `إحداثيات: ${lat.toFixed(5)}, ${lng.toFixed(5)}` }));
      setStatusMessage({ type: "error", text: "تم حفظ الموقع، لكن تعذر التحقق من المحافظة تلقائياً." });
      setTimeout(() => setStatusMessage({ type: "", text: "" }), 3000);
    }
  };
 const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!navigator.onLine) return setStatusMessage({ type: "error", text: "لا يوجد اتصال بالإنترنت." });
    
    if (!formData.city) {
        return setStatusMessage({ type: "error", text: "الرجاء اختيار المحافظة." });
    }
    if (!formData.address || formData.address.trim() === "" || !formData.residence_lat) {
      return setStatusMessage({ type: "error", text: "الرجاء تحديد العنوان التفصيلي والسكن عبر النقر على الخريطة." });
    }

    if (isSubmitting) return; 
    
    setIsSubmitting(true);
    setStatusMessage({ type: "loading", text: "جارِ تشفير وإرسال البيانات بأمان..." });

    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("NO_TOKEN");

      const response = await fetch(`${API_BASE_URL}/patients/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ ...step1Data, ...formData }),
      });

      if (!response.ok) {
        if (response.status === 401) throw new Error("EXPIRED_SESSION"); 
        if (response.status === 403) throw new Error("FORBIDDEN"); 
        if (response.status === 404) throw new Error("NOT_FOUND"); 
        
        // 🔹 استخراج خطأ Django (DRF) الدقيق وعرضه بدلاً من الرسالة العامة
        if (response.status === 400 || response.status === 409) {
          const errData = await response.json();
          let detailedError = "INVALID_DATA";
          
          if (errData && typeof errData === 'object') {
            if (errData.detail) {
              detailedError = errData.detail;
            } else {
              const messages = [];
              for (const [key, value] of Object.entries(errData)) {
                const fieldName = key === 'non_field_errors' ? 'خطأ عام' : key;
                const fieldError = Array.isArray(value) ? value.join(' و ') : value;
                messages.push(`${fieldName}: ${fieldError}`);
              }
              if (messages.length > 0) detailedError = messages.join(' | ');
            }
          }
          throw new Error(detailedError); 
        }

        throw new Error("SERVER_ERROR"); 
      }

      setStatusMessage({ type: "success", text: "تم إضافة المريض بنجاح! جاري تحويلك..." });
      const userRole = localStorage.getItem("user_role");
        
        setTimeout(() => {
        // جلب دور المستخدم من التخزين المحلي (كما هو معتمد في المشروع)
        const userRole = localStorage.getItem("user_role");
        
        // التوجيه بناءً على الدور
        if (userRole === "admin") {
          navigate("/all-patients");
        } else {
          navigate("/Main");
        }
      }, 2000);
    } catch (err) {
      setIsSubmitting(false); 
      switch (err.message) {
        case "NO_TOKEN":
        case "EXPIRED_SESSION":
          setStatusMessage({ type: "error", text: "انتهت الجلسة. سيتم تحويلك لتسجيل الدخول." });
          localStorage.removeItem("access_token");
          setTimeout(() => navigate("/login"), 3000);
          break;
        case "FORBIDDEN": setStatusMessage({ type: "error", text: "ليس لديك الصلاحيات الكافية." }); break;
        case "SERVER_ERROR": setStatusMessage({ type: "error", text: "حدث خطأ داخلي في الخادم." }); break;
        case "Failed to fetch": setStatusMessage({ type: "error", text: "الخادم لا يستجيب." }); break;
        // 🔹 طباعة الخطأ القادم من السيرفر مباشرة لتسهيل التتبع
        default: setStatusMessage({ type: "error", text: err.message === "INVALID_DATA" ? "يوجد خطأ في البيانات." : err.message });
      }
    }
  };

  // 🔹 فصلنا نافذة الخريطة كمتغير لتضمينها في الموبايل والحاسوب 🔹
  const mapModalContent = showMapModal && (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-5xl h-[80vh] rounded-2xl shadow-2xl overflow-hidden border border-white/20">
        <MapArea mode="picker" onConfirm={handleMapConfirm} onCancel={() => setShowMapModal(false)} />
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <AddPatientMobileStep2 
          formData={formData} 
          handleChange={handleChange} 
          onSubmit={handleSubmit} 
          isSubmitting={isSubmitting} 
          statusMessage={statusMessage} 
          openMapForAddress={openMapForAddress} 
        />
        {/* 🔹 تمت إضافة الخريطة هنا لتعمل في الموبايل 🔹 */}
        {mapModalContent}
      </>
    );
  }

  return (
    <div className="add-patient-container" dir="rtl">
      <div className="add-patient-left">
        <form className="add-patient-form" onSubmit={handleSubmit}>
          <h3 className="add-patient-h3">معلومات السكن</h3>

          {statusMessage.text && (
            <div className={`status-alert alert-${statusMessage.type}`} style={{ marginBottom: '15px' }}>
              {statusMessage.type === 'loading' && <i className="fa-solid fa-circle-notch fa-spin"></i>}
              {statusMessage.type === 'error' && <i className="fa-solid fa-triangle-exclamation"></i>}
              {statusMessage.type === 'success' && <i className="fa-solid fa-circle-check"></i>}
              <span style={{marginLeft: '10px'}}>{statusMessage.text}</span>
            </div>
          )}

          <div className="add-patient-input-group">
             <i className="fa-solid fa-city"></i>
             <select 
               name="city" 
               value={formData.city} 
               onChange={handleChange} 
               disabled={isSubmitting}
               style={{width: '100%', padding: '14px 45px 14px 16px', borderRadius: '12px', border: '1.5px solid #e3edf5', outline: 'none', backgroundColor: '#ffffff', color: '#333'}}
             >
               <option value="">اختر المحافظة...</option>
               {SYRIAN_GOVERNORATES.map(gov => (
                 <option key={gov} value={gov}>{gov}</option>
               ))}
             </select>
          </div>
          <div className="add-patient-input-group">
            <i className="fa-solid fa-map-location-dot"></i>
            <input
              type="text"
              name="address"
              placeholder="العنوان التفصيلي - السكن (انقر للتحديد من الخريطة)"
              value={formData.address}
              onClick={() => !isSubmitting && openMapForAddress()}
              readOnly
              disabled={isSubmitting}
              style={{ cursor: isSubmitting ? 'wait' : 'pointer', backgroundColor: '#ffffff' }}
            />
          </div>
          <div className="add-patient-input-group">
            <i className="fa-solid fa-droplet"></i>
            <select name="blood_group" value={formData.blood_group} onChange={handleChange} disabled={isSubmitting} style={{width: '100%', padding: '14px 45px 14px 16px', borderRadius: '12px', border: '1.5px solid #e3edf5', outline: 'none'}}>
              <option value="">زمرة الدم (غير محدد)</option>
              <option value="A+">A+</option>
              <option value="O+">O+</option>
              <option value="B+">B+</option>
              <option value="AB+">AB+</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '15px', marginTop: '10px' }}>
            <button type="submit" className="add-patient-submit" disabled={isSubmitting} style={{ flex: 1, opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'wait' : 'pointer' }}>
              {isSubmitting ? 'جاري الإضافة...' : 'حفظ بيانات المريض'}
            </button>
            <button type="button" className="add-patient-submit" onClick={() => navigate(-1)} disabled={isSubmitting} style={{ flex: 1, backgroundColor: '#8fa5b3', opacity: isSubmitting ? 0.5 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
              السابق
            </button>
          </div>
        </form>
      </div>

      <div className="add-patient-right">
        <img className='syria-logo-add-patient-logo' src="/images/Emblem_of_Syria_(2025–present).svg.png" alt="Syria Emblem" />
        <h1 className="add-patient-title">الجمهورية العربية السورية</h1>
        <h4 className="add-patient-sub">وزارة الصحة - منصة التنبؤ بالأوبئة</h4>
      </div>

      {/* 🔹 تمت إضافة الخريطة هنا لتعمل في الحاسوب 🔹 */}
      {mapModalContent}
    </div>
  );
}